import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { whatsappApi } from '@/services/api';
import Sidebar from '@/components/common/Sidebar';
import TrialBanner from '@/components/common/TrialBanner';
import { useUIStore } from '@/store/uiStore';
import {
  Menu, Loader2, MessageCircle, Check, X, RefreshCw, Eye,
} from 'lucide-react';

type WaOrder = {
  name: string;
  status: string;
  customer?: string;
  customer_name?: string;
  customer_mobile: string;
  grand_total: number;
  currency?: string;
  payment_mode?: string;
  pos_invoice?: string;
  creation: string;
  delivery_notes?: string;
};

export default function WhatsAppOrdersPage() {
  const { t } = useTranslation();
  const { toggleSidebar } = useUIStore();
  const qc = useQueryClient();
  const [status, setStatus] = useState('Pending');
  const [detail, setDetail] = useState<any | null>(null);

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['whatsappOrders', status],
    queryFn: async () => {
      const res = await whatsappApi.getOrders({ status: status || undefined, limit: 100 });
      return res.data.message as { orders: WaOrder[]; total: number };
    },
    refetchInterval: 30000,
  });

  const confirmMutation = useMutation({
    mutationFn: (name: string) => whatsappApi.confirmOrder(name),
    onSuccess: (res) => {
      toast.success(t('whatsapp.orderConfirmed', { invoice: res.data.message?.invoice }));
      void qc.invalidateQueries({ queryKey: ['whatsappOrders'] });
      setDetail(null);
    },
    onError: (err: any) => toast.error(err.response?.data?.message || err.message),
  });

  const rejectMutation = useMutation({
    mutationFn: (name: string) => whatsappApi.rejectOrder(name),
    onSuccess: () => {
      toast.success(t('whatsapp.orderRejected'));
      void qc.invalidateQueries({ queryKey: ['whatsappOrders'] });
      setDetail(null);
    },
    onError: (err: any) => toast.error(err.response?.data?.message || err.message),
  });

  const openDetail = async (name: string) => {
    try {
      const res = await whatsappApi.getOrder(name);
      setDetail(res.data.message);
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message);
    }
  };

  const orders = data?.orders ?? [];

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <TrialBanner />
        <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
          <button type="button" onClick={toggleSidebar} className="lg:hidden p-2 rounded-lg hover:bg-gray-100">
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-emerald-600" />
              {t('whatsapp.ordersTitle')}
            </h1>
            <p className="text-sm text-gray-500">{t('whatsapp.ordersSubtitle')}</p>
          </div>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="input-field text-sm w-40"
          >
            <option value="Pending">{t('whatsapp.statusPending')}</option>
            <option value="Confirmed">{t('whatsapp.statusConfirmed')}</option>
            <option value="Rejected">{t('whatsapp.statusRejected')}</option>
            <option value="">{t('whatsapp.statusAll')}</option>
          </select>
          <button type="button" onClick={() => refetch()} className="btn-secondary flex items-center gap-1.5">
            {isFetching ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </button>
        </header>

        <main className="flex-1 p-4 overflow-auto">
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {isLoading ? (
              <div className="flex items-center justify-center py-16 text-gray-500">
                <Loader2 className="w-6 h-6 animate-spin mr-2" /> {t('common.loading')}
              </div>
            ) : orders.length === 0 ? (
              <div className="p-12 text-center text-gray-500">
                <MessageCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>{t('whatsapp.noOrders')}</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-700 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left">{t('whatsapp.orderId')}</th>
                    <th className="px-4 py-3 text-left">{t('whatsapp.customer')}</th>
                    <th className="px-4 py-3 text-left">{t('whatsapp.mobile')}</th>
                    <th className="px-4 py-3 text-right">{t('whatsapp.total')}</th>
                    <th className="px-4 py-3 text-left">{t('whatsapp.status')}</th>
                    <th className="px-4 py-3 text-right">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {orders.map((o) => (
                    <tr key={o.name} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900">{o.name}</td>
                      <td className="px-4 py-3">{o.customer_name || '—'}</td>
                      <td className="px-4 py-3">{o.customer_mobile}</td>
                      <td className="px-4 py-3 text-right font-semibold text-gray-900">
                        {Number(o.grand_total || 0).toFixed(2)} {o.currency || ''}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          o.status === 'Pending' ? 'bg-amber-100 text-amber-800'
                            : o.status === 'Confirmed' ? 'bg-emerald-100 text-emerald-800'
                              : 'bg-gray-100 text-gray-700'
                        }`}>
                          {o.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button type="button" onClick={() => openDetail(o.name)} className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg" title={t('common.view')}>
                            <Eye className="w-4 h-4" />
                          </button>
                          {o.status === 'Pending' && (
                            <>
                              <button
                                type="button"
                                disabled={confirmMutation.isPending}
                                onClick={() => confirmMutation.mutate(o.name)}
                                className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg"
                                title={t('whatsapp.confirm')}
                              >
                                <Check className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                disabled={rejectMutation.isPending}
                                onClick={() => rejectMutation.mutate(o.name)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                title={t('whatsapp.reject')}
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </main>
      </div>

      {detail && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">{detail.name}</h3>
              <button type="button" onClick={() => setDetail(null)} className="p-1.5 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <p><span className="text-gray-500">{t('whatsapp.customer')}:</span> {detail.customer_name} ({detail.customer_mobile})</p>
              <p><span className="text-gray-500">{t('whatsapp.status')}:</span> {detail.status}</p>
              <p><span className="text-gray-500">{t('whatsapp.total')}:</span> {Number(detail.grand_total || 0).toFixed(2)} {detail.currency}</p>
              {detail.pos_invoice && <p><span className="text-gray-500">{t('whatsapp.invoice')}:</span> {detail.pos_invoice}</p>}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50"><tr><th className="px-3 py-2 text-left">Item</th><th className="px-3 py-2 text-right">Qty</th><th className="px-3 py-2 text-right">Rate</th></tr></thead>
                  <tbody>
                    {(detail.items || []).map((it: any, idx: number) => (
                      <tr key={idx} className="border-t"><td className="px-3 py-2">{it.item_name}</td><td className="px-3 py-2 text-right">{it.qty}</td><td className="px-3 py-2 text-right">{Number(it.rate).toFixed(2)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {detail.status === 'Pending' && (
                <div className="flex gap-2 pt-2">
                  <button type="button" className="btn-primary flex-1" disabled={confirmMutation.isPending} onClick={() => confirmMutation.mutate(detail.name)}>
                    {t('whatsapp.confirm')}
                  </button>
                  <button type="button" className="btn-secondary flex-1" disabled={rejectMutation.isPending} onClick={() => rejectMutation.mutate(detail.name)}>
                    {t('whatsapp.reject')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
